// (c) 2026 Owl Consultations. All rights reserved.
// Proprietary and confidential — licensed use only.

// D-CRX-89 — one protocol slot on the drug record (Premedication, Anaphylaxis kit or Flushing): pick the
// drug's preset, create a new protocol, edit the assigned one (a new version for every drug using it), remove
// it from this drug, or retire it. The pick and "Remove from this drug" are saved with the drug like every
// other field; create / edit / retire are saved at once, audited, and this drug's slot follows them.

import { useState } from 'react'
import Box from '@mui/material/Box'
import Stack from '@mui/material/Stack'
import AppButton from '../../../components/ui/AppButton'
import SearchableSelect from '../../../components/common/SearchableSelect'
import type { AdminProtocol, AdminProtocolComponent } from '../../../api/client'
import { SLOT_CATEGORY, type ProtocolSlot } from '../../../api/carerxProtocols'
import { useSlotImpact } from '../../../hooks/queries/carerxProtocols'
import ProtocolEditorDialog, { SLOT_LABEL } from './ProtocolEditorDialog'
import ProtocolRetireDialog from './ProtocolRetireDialog'

export default function DrugProtocolSlot({ slot, value, savedValue, protocols, components, drugReferenceId, readOnly = false, onChange, children }: {
  slot: ProtocolSlot
  value: string | null
  /** The slot as saved on the drug (null = empty) — assigning to an empty slot never reaches enrolled patients. */
  savedValue?: string | null
  /** The pharmacy's current library (all categories). */
  protocols: AdminProtocol[]
  components: AdminProtocolComponent[]
  drugReferenceId: string | null
  /** Not a pharmacy admin: create / edit / retire are admin-only on the server, so none are offered. */
  readOnly?: boolean
  onChange: (id: string | null) => void
  /** The assigned protocol's lines, rendered by the caller (premed/kit keep their per-drug added lines). */
  children?: React.ReactNode
}) {
  const [editor, setEditor] = useState<'new' | 'edit' | null>(null)
  const [retiring, setRetiring] = useState(false)

  const inCategory = protocols.filter(p => p.category === SLOT_CATEGORY[slot])
  const options = inCategory.filter(p => p.active)
  const assigned = inCategory.find(p => p.id === value)
  const label = SLOT_LABEL[slot]
  const stale = value !== null && !assigned
  // Round-2 addendum (owner 2026-10-29): a protocol added to an EMPTY slot reaches new enrollments only.
  const emptyOnDrug = savedValue === null || savedValue === undefined
  // The impact count is admin-only on the server, and only matters to someone who can assign.
  const slotImpact = useSlotImpact(drugReferenceId, slot, emptyOnDrug && !readOnly)
  const without = slotImpact.data?.enrolled_without ?? 0

  return (
    <Box data-testid={`protocol-slot-${slot}`} sx={{ mb: 2 }}>
      <Box sx={{ fontSize: 12, fontWeight: 700, mb: 0.5 }}>{label} protocol</Box>
      <SearchableSelect ariaLabel={`${label} protocol`} value={value ?? ''}
        onChange={v => onChange(v || null)}
        options={[{ value: '', label: '— none —' }, ...options.map(p => ({
          value: p.id,
          label: `${p.name} · v${p.version}${p.scope === 'global' ? ' · template' : ''}${p.lines.length === 0 ? ' (empty)' : ''}`,
        }))]} />
      {assigned && !assigned.active && (
        <Box sx={{ fontSize: 12, color: '#b23a48', mt: 0.5 }}>This protocol is retired. Pick another or remove it.</Box>
      )}
      {stale && (
        <Box sx={{ fontSize: 12, color: '#93610f', mt: 0.5 }}>
          The assigned protocol is not a current version in this pharmacy's library. Pick the current one.
        </Box>
      )}
      {!readOnly && <Stack direction="row" spacing={1} sx={{ mt: 1, flexWrap: 'wrap' }}>
        <AppButton size="small" variant="outlined" onClick={() => setEditor('new')}>+ New protocol</AppButton>
        {assigned && <AppButton size="small" variant="outlined" onClick={() => setEditor('edit')}>Edit</AppButton>}
        {value && <AppButton size="small" onClick={() => onChange(null)}>Remove from this drug</AppButton>}
        {assigned?.active && <AppButton size="small" color="error" onClick={() => setRetiring(true)}>Retire</AppButton>}
      </Stack>}
      {value && !readOnly && <Box sx={{ fontSize: 11.5, color: 'var(--color-text-soft)', mt: 0.5 }}>Picking or removing is saved with the drug.</Box>}
      {emptyOnDrug && without > 0 && (
        <Box data-testid={`slot-impact-${slot}`} sx={{ fontSize: 12, color: '#93610f', mt: 0.5 }}>
          {without} enrolled patient{without === 1 ? '' : 's'} keep{without === 1 ? 's' : ''} no {label.toLowerCase()} protocol;
          {' '}new enrollments get the one you assign here.
        </Box>
      )}
      {children}

      {editor && (
        <ProtocolEditorDialog open slot={slot} protocol={editor === 'edit' ? assigned ?? null : null}
          components={components} drugReferenceId={drugReferenceId}
          onClose={() => setEditor(null)}
          onSaved={s => { setEditor(null); onChange(s.id) }} />
      )}

      <ProtocolRetireDialog protocol={retiring && assigned ? assigned : null}
        onClose={() => setRetiring(false)} onRetired={() => onChange(null)} />
    </Box>
  )
}
